/*
 * The end of a session reaches every tab of this browser, so that nothing of the person stays
 * on screen or in memory (PRD 05 §2: Noa's conversation disappears on sign-out). The other tabs
 * hear it through a BroadcastChannel, or through a storage event where there is none.
 */

const CHANNEL = 'eklevo-auth'
const STORAGE_KEY = 'eklevo.signedOut'
/** A BroadcastChannel also reaches the other channels of the sending page: skip our own. */
const TAB = crypto.randomUUID()

interface SignedOut {
  type: 'signed-out'
  tab: string
}

const listeners = new Set<() => void>()

function fromOtherTab(data: unknown): boolean {
  const message = data as Partial<SignedOut> | null
  return message?.type === 'signed-out' && message.tab !== TAB
}

/** The session ended in this tab (signed out, or refused by the server): every tab lets go. */
export function endSession(): void {
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL)
    channel.postMessage({ type: 'signed-out', tab: TAB } satisfies SignedOut)
    channel.close()
  } else {
    try {
      // The storage event fires in the other tabs only; the value itself means nothing.
      localStorage.setItem(STORAGE_KEY, TAB)
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage blocked: the other tabs find out on their next request or when shown again.
    }
  }
  for (const listener of [...listeners]) listener()
}

/** Calls `handler` when the session ends in this tab or in another one; returns the unsubscribe. */
export function onSessionEnd(handler: () => void): () => void {
  listeners.add(handler)
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = (e: MessageEvent) => {
      if (fromOtherTab(e.data)) handler()
    }
    return () => {
      listeners.delete(handler)
      channel.close()
    }
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) handler()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(handler)
    window.removeEventListener('storage', onStorage)
  }
}
